import React, { useState, useEffect, useRef, useCallback } from 'react'
import MicButton from './MicButton'
import {
  BarChart3, MessageCircle, BookOpen, Users, Loader2,
  Send, Clock, Play, Square, ChevronRight,
  Plus, X, Award, Target, Brain, Zap,
  AlertTriangle, FileText, Trophy, Flame, Star,
  CheckCircle, Eye, Edit3, ThumbsUp, RefreshCw,
} from 'lucide-react'
import { api } from '../lib/api'
import { formatRelativeTime, getInitials } from '../utils/format'
import './KEE.css'

const TABS = [
  { key: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  { key: 'interview', label: 'Interview', icon: MessageCircle },
  { key: 'cards', label: 'Knowledge Cards', icon: BookOpen },
  { key: 'pain-points', label: 'Pain Points', icon: AlertTriangle },
  { key: 'leaderboard', label: 'Leaderboard', icon: Award },
  { key: 'sops', label: 'SOPs', icon: FileText },
  { key: 'people', label: 'People', icon: Users },
  { key: 'contradictions', label: 'Contradictions', icon: AlertTriangle },
]

const CATEGORY_LABELS = {
  business_strategy: 'Business Strategy',
  risk_decision_making: 'Risk & Decisions',
  estimating_pricing: 'Estimating & Pricing',
  client_relationships: 'Client Relations',
  vendor_subcontractor: 'Vendors & Subs',
  operations_field: 'Field Operations',
  project_management: 'Project Mgmt',
  financial_management: 'Financial Mgmt',
  hr_people: 'HR & People',
  admin_office: 'Admin & Office',
  legal_contracts: 'Legal & Contracts',
  safety_compliance: 'Safety & Compliance',
  lessons_learned: 'Lessons Learned',
}

const ROLE_TYPES = [
  { value: 'owner', label: 'Owner' },
  { value: 'executive', label: 'Executive' },
  { value: 'manager', label: 'Manager' },
  { value: 'individual_contributor', label: 'Individual Contributor' },
]

const ROLE_CATEGORIES = [
  { value: 'executive', label: 'Executive' },
  { value: 'operations', label: 'Operations' },
  { value: 'field', label: 'Field' },
  { value: 'office', label: 'Office' },
  { value: 'sales', label: 'Sales' },
]

const TITLE_PROGRESSION = [
  { key: 'apprentice', label: 'Apprentice', color: '#78909C', minSessions: 0 },
  { key: 'journeyman', label: 'Journeyman', color: '#42A5F5', minSessions: 30 },
  { key: 'foreman', label: 'Foreman', color: '#66BB6A', minSessions: 100 },
  { key: 'superintendent', label: 'Superintendent', color: '#FFA726', minSessions: 250 },
  { key: 'master_builder', label: 'Master Builder', color: '#AB47BC', minSessions: 500 },
]

const SEVERITY_COLORS = {
  critical: { bg: '#EF535022', color: '#EF5350' },
  high: { bg: '#FFA72622', color: '#FFA726' },
  medium: { bg: '#42A5F522', color: '#42A5F5' },
  low: { bg: '#78909C22', color: '#78909C' },
}

const STATUS_COLORS = {
  captured: { bg: '#78909C22', color: '#78909C' },
  validated: { bg: '#42A5F522', color: '#42A5F5' },
  prioritized: { bg: '#FFA72622', color: '#FFA726' },
  in_progress: { bg: '#AB47BC22', color: '#AB47BC' },
  resolved: { bg: '#66BB6A22', color: '#66BB6A' },
}

function gapColor(pct) {
  if (pct >= 70) return '#66BB6A'
  if (pct >= 40) return '#FFA726'
  return '#EF5350'
}

function getTitleInfo(titleKey) {
  return TITLE_PROGRESSION.find(t => t.key === titleKey) || TITLE_PROGRESSION[0]
}

// ────────────────────────────────────────────────
// MAIN COMPONENT
// ────────────────────────────────────────────────


// ── Contradictions Review ──────────────────────────────────────────────────

function ContradictionsReview() {
  const [contradictions, setContradictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showResolved, setShowResolved] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [resolvedAnswer, setResolvedAnswer] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadContradictions();
  }, [showResolved]);

  async function loadContradictions() {
    setLoading(true);
    try {
      const res = await api.get('/kee/contradictions?status=' + (showResolved ? 'resolved' : 'open'));
      setContradictions(res.contradictions || []);
    } catch {}
    setLoading(false);
  }

  async function resolve(id) {
    if (!resolvedAnswer.trim()) return;
    setSaving(true);
    try {
      await api.post('/kee/contradictions/' + id + '/resolve', { resolved_answer: resolvedAnswer });
      setEditingId(null);
      setResolvedAnswer('');
      loadContradictions();
    } catch {}
    setSaving(false);
  }

  if (loading) return React.createElement('div', { className: 'text-center py-8 text-gray-400 text-sm' }, 'Loading contradictions...');

  return React.createElement('div', { className: 'space-y-4' },
    React.createElement('div', { className: 'flex items-center justify-between' },
      React.createElement('h3', { className: 'text-sm font-bold text-gray-900 dark:text-gray-100' },
        showResolved ? 'Resolved Contradictions' : 'Open Contradictions (' + contradictions.length + ')'
      ),
      React.createElement('button', {
        onClick: () => setShowResolved(!showResolved),
        className: 'text-xs px-2 py-1 rounded border ' + (showResolved ? 'bg-gray-100 text-gray-600 border-gray-300' : 'text-gray-400 border-gray-200')
      }, showResolved ? 'Show Open' : 'Show Resolved')
    ),

    contradictions.length === 0
      ? React.createElement('div', { className: 'text-center py-12 border border-dashed border-gray-200 dark:border-slate-700 rounded-lg' },
          React.createElement('p', { className: 'text-sm text-gray-500' }, showResolved ? 'No resolved contradictions yet.' : 'No contradictions detected yet. As more people answer questions about the same topics, conflicts will surface here.')
        )
      : contradictions.map(c => React.createElement('div', {
          key: c.id,
          className: 'border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden'
        },
          // Title bar
          React.createElement('div', { className: 'px-4 py-3 bg-orange-50 dark:bg-orange-900/10 border-b border-orange-200 dark:border-orange-800' },
            React.createElement('div', { className: 'flex items-center justify-between' },
              React.createElement('h4', { className: 'text-sm font-semibold text-gray-900 dark:text-gray-100' }, c.title),
              c.status === 'resolved'
                ? React.createElement('span', { className: 'text-[10px] font-bold px-2 py-0.5 rounded bg-green-100 text-green-700' }, 'Resolved')
                : React.createElement('span', { className: 'text-[10px] font-bold px-2 py-0.5 rounded bg-orange-100 text-orange-700' }, 'Open')
            ),
            c.category && React.createElement('span', { className: 'text-[10px] text-gray-400' }, c.category.replace(/_/g, ' '))
          ),

          // Two columns: Person A vs Person B
          React.createElement('div', { className: 'grid grid-cols-2 divide-x divide-gray-200 dark:divide-slate-700' },
            React.createElement('div', { className: 'p-4' },
              React.createElement('p', { className: 'text-[10px] font-bold uppercase tracking-wide text-blue-600 mb-1' }, c.card_a_person_name),
              React.createElement('p', { className: 'text-xs text-gray-700 dark:text-gray-300 leading-relaxed' }, c.card_a_content)
            ),
            React.createElement('div', { className: 'p-4' },
              React.createElement('p', { className: 'text-[10px] font-bold uppercase tracking-wide text-purple-600 mb-1' }, c.card_b_person_name),
              React.createElement('p', { className: 'text-xs text-gray-700 dark:text-gray-300 leading-relaxed' }, c.card_b_content)
            )
          ),

          // AI Proposed Answer
          c.ai_proposed_answer && React.createElement('div', { className: 'px-4 py-3 bg-blue-50 dark:bg-blue-900/10 border-t border-blue-200 dark:border-blue-800' },
            React.createElement('p', { className: 'text-[10px] font-bold uppercase tracking-wide text-blue-600 mb-1' }, 'AI Proposed Answer'),
            React.createElement('p', { className: 'text-xs text-gray-700 dark:text-gray-300 leading-relaxed' }, c.ai_proposed_answer)
          ),

          // Resolved answer or resolve action
          c.status === 'resolved'
            ? React.createElement('div', { className: 'px-4 py-3 bg-green-50 dark:bg-green-900/10 border-t border-green-200' },
                React.createElement('p', { className: 'text-[10px] font-bold uppercase tracking-wide text-green-600 mb-1' }, 'Team Decision'),
                React.createElement('p', { className: 'text-xs text-gray-700 dark:text-gray-300' }, c.resolved_answer)
              )
            : React.createElement('div', { className: 'px-4 py-3 border-t border-gray-200 dark:border-slate-700' },
                editingId === c.id
                  ? React.createElement('div', { className: 'space-y-2' },
                      React.createElement('textarea', {
                        rows: 3,
                        value: resolvedAnswer,
                        onChange: e => setResolvedAnswer(e.target.value),
                        placeholder: 'Enter the correct answer...',
                        className: 'w-full px-3 py-2 text-sm border border-gray-200 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100'
                      }),
                      React.createElement('div', { className: 'flex gap-2' },
                        React.createElement('button', {
                          onClick: () => resolve(c.id),
                          disabled: saving || !resolvedAnswer.trim(),
                          className: 'px-3 py-1.5 text-xs font-semibold bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50'
                        }, saving ? 'Saving...' : 'Resolve with this answer'),
                        React.createElement('button', {
                          onClick: () => { setEditingId(null); setResolvedAnswer(''); },
                          className: 'px-3 py-1.5 text-xs text-gray-500 border border-gray-200 rounded'
                        }, 'Cancel')
                      )
                    )
                  : React.createElement('button', {
                      onClick: () => { setEditingId(c.id); setResolvedAnswer(c.ai_proposed_answer || ''); },
                      className: 'text-xs font-semibold text-orange-600 hover:text-orange-800'
                    }, 'Resolve this contradiction')
              )
        ))
  );
}

export default function KEEPage() {
  const [tab, setTab] = useState('dashboard')
  const [stats, setStats] = useState(null)
  const [people, setPeople] = useState([])
  const [mySession, setMySession] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([
      api.get('/kee/stats'),
      api.get('/kee/people'),
      api.get('/kee/my-session').catch(() => null),
    ]).then(([s, p, ms]) => {
      setStats(s)
      setPeople(p.people || [])
      setMySession(ms)
    }).catch(console.error)
      .finally(() => setLoading(false))
  }, [])

  const refreshPeople = useCallback(async () => {
    const p = await api.get('/kee/people')
    setPeople(p.people || [])
  }, [])

  const refreshStats = useCallback(async () => {
    const [s, ms] = await Promise.all([
      api.get('/kee/stats'),
      api.get('/kee/my-session').catch(() => null),
    ])
    setStats(s)
    setMySession(ms)
  }, [])

  if (loading) {
    return (
      <div className="kee-page">
        <div className="kee-loading"><Loader2 size={24} /></div>
      </div>
    )
  }

  return (
    <div className="kee-page">
      <div className="kee-tabs">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            className={`kee-tab ${tab === key ? 'active' : ''}`}
            onClick={() => setTab(key)}
          >
            <Icon size={16} />
            <span className="kee-tab-label">{label}</span>
            {key === 'interview' && mySession?.hasPendingSession && (
              <span className="kee-tab-badge">{mySession.questions.length}</span>
            )}
          </button>
        ))}
      </div>

      <div className="kee-content">
        {tab === 'dashboard' && (
          <DashboardTab stats={stats} people={people} mySession={mySession} onGoToInterview={() => setTab('interview')} />
        )}
        {tab === 'interview' && (
          <InterviewTab people={people} mySession={mySession} onComplete={() => refreshStats()} />
        )}
        {tab === 'cards' && <CardsTab />}
        {tab === 'pain-points' && <PainPointsTab />}
        {tab === 'leaderboard' && <LeaderboardTab mySession={mySession} />}
        {tab === 'sops' && <SOPsTab />}
        {tab === 'people' && <PeopleTab people={people} onRefresh={refreshPeople} />}
        {tab === 'contradictions' && <ContradictionsReview />}
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────
// DASHBOARD TAB
// ────────────────────────────────────────────────

function DashboardTab({ stats, people, mySession, onGoToInterview }) {
  const [selectedPerson, setSelectedPerson] = useState(null)
  const [gaps, setGaps] = useState(null)
  const [sessions, setSessions] = useState([])

  useEffect(() => {
    const person = selectedPerson || people[0]
    if (!person) return
    Promise.all([
      api.get(`/kee/gaps/${person.id}`),
      api.get(`/kee/sessions?person_id=${person.id}&limit=5`),
    ]).then(([g, s]) => {
      setGaps(g.gaps)
      setSessions(s.sessions || [])
    }).catch(console.error)
  }, [selectedPerson, people])

  const person = selectedPerson || people[0]

  return (
    <>
      {/* My Session notification */}
      {mySession?.hasPendingSession && (
        <div className="kee-notification-card">
          <div className="kee-notification-icon">
            <Brain size={24} />
          </div>
          <div className="kee-notification-content">
            <div className="kee-notification-title">
              You have {mySession.questions.length} question{mySession.questions.length !== 1 ? 's' : ''} waiting
            </div>
            <div className="kee-notification-sub">
              {mySession.gamification?.current_streak > 0
                ? `Current streak: ${mySession.gamification.current_streak} days — keep it going!`
                : 'Start your daily knowledge session — it only takes 3-5 minutes'}
            </div>
          </div>
          <button className="kee-start-btn primary" onClick={onGoToInterview}>
            <Zap size={16} /> Start Session
          </button>
        </div>
      )}

      {/* Stat cards */}
      <div className="kee-stats-row">
        <div className="kee-stat-card">
          <div className="label">Participants</div>
          <div className="value">{stats?.participants ?? 0}</div>
        </div>
        <div className="kee-stat-card">
          <div className="label">Total Sessions</div>
          <div className="value">{stats?.totalSessions ?? 0}</div>
        </div>
        <div className="kee-stat-card">
          <div className="label">Knowledge Cards</div>
          <div className="value">{stats?.totalCards ?? 0}</div>
        </div>
        <div className="kee-stat-card">
          <div className="label">Pain Points</div>
          <div className="value">{stats?.totalPainPoints ?? 0}</div>
        </div>
        <div className="kee-stat-card">
          <div className="label">SOPs Generated</div>
          <div className="value">{stats?.totalSOPs ?? 0}</div>
        </div>
      </div>

      {/* Person selector + gap analysis */}
      {person && (
        <>
          <div className="kee-section-header">
            <div className="kee-section-title">Knowledge Coverage — {person.name}</div>
            {people.length > 1 && (
              <select
                value={person.id}
                onChange={e => setSelectedPerson(people.find(p => p.id === e.target.value))}
                className="kee-select-inline"
              >
                {people.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            )}
          </div>

          {gaps && (
            <div className="kee-surface-card" style={{ marginBottom: '1.5rem' }}>
              <div className="kee-gap-grid">
                {Object.entries(gaps).map(([cat, pct]) => (
                  <div className="kee-gap-item" key={cat}>
                    <span className="kee-gap-label">
                      {CATEGORY_LABELS[cat] || cat.replace(/_/g, ' ')}
                    </span>
                    <div className="kee-gap-bar-bg">
                      <div
                        className="kee-gap-bar-fill"
                        style={{ width: `${Math.max(pct, 2)}%`, background: gapColor(pct) }}
                      />
                    </div>
                    <span className="kee-gap-pct">{pct}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Recent sessions */}
      <div className="kee-section-header">
        <div className="kee-section-title">Recent Sessions</div>
      </div>
      {sessions.length === 0 ? (
        <div className="kee-empty">
          <Clock size={24} />
          <span>No sessions yet. Start an interview to begin extracting knowledge.</span>
        </div>
      ) : (
        <div className="kee-sessions-list">
          {sessions.map(s => (
            <div className="kee-session-row" key={s.id}>
              <span className="type-badge">{s.session_type?.replace(/_/g, ' ')}</span>
              <span style={{ flex: 1, color: 'var(--color-text)' }}>
                {s.person?.name || 'Unknown'}
              </span>
              <span className="cards-count">
                {s.knowledge_cards_generated} cards, {s.pain_points_captured} pain points
              </span>
              <span style={{ color: 'var(--color-text-muted)', fontSize: '0.75rem' }}>
                {formatRelativeTime(s.started_at)}
              </span>
            </div>
          ))}
        </div>
      )}
    </>
  )
}

// ────────────────────────────────────────────────
// INTERVIEW TAB
// ────────────────────────────────────────────────

function InterviewTab({ people, mySession, onComplete }) {
  const [session, setSession] = useState(null)
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [starting, setStarting] = useState(false)
  const [completing, setCompleting] = useState(false)
  const [selectedPerson, setSelectedPerson] = useState('')
  const messagesEndRef = useRef(null)
  const textareaRef = useRef(null)

  // Auto-select self if user is a KEE participant
  useEffect(() => {
    if (mySession?.person) {
      setSelectedPerson(mySession.person.id)
    } else if (people.length > 0 && !selectedPerson) {
      setSelectedPerson(people[0].id)
    }
  }, [mySession, people])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 192) + 'px'
    }
  }, [input])

  const isMyself = mySession?.person?.id === selectedPerson

  async function startSessionWithType(type) {
    if (!selectedPerson) return
    setStarting(true)
    try {
      const res = await api.post('/kee/sessions/start', {
        person_id: selectedPerson,
        session_type: type,
        mode: 'text',
      })
      setSession(res.session)

      const firstMsg = [{ role: 'user', content: '[Session started — please greet me and ask your first question]' }]
      const reply = await api.post(`/kee/sessions/${res.session.id}/message`, {
        messages: firstMsg,
      })
      setMessages([{ role: 'assistant', content: reply.response }])
    } catch (err) {
      console.error('[KEE] Start session error:', err)
    } finally {
      setStarting(false)
    }
  }

  async function sendMessage() {
    const text = input.trim()
    if (!text || !session || sending) return

    const newMessages = [...messages, { role: 'user', content: text }]
    setMessages(newMessages)
    setInput('')
    setSending(true)

    try {
      const reply = await api.post(`/kee/sessions/${session.id}/message`, {
        messages: newMessages,
      })
      setMessages(prev => [...prev, { role: 'assistant', content: reply.response }])
    } catch (err) {
      console.error('[KEE] Message error:', err)
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I hit an error. Let\'s keep going — try again?' }])
    } finally {
      setSending(false)
    }
  }

  async function completeSession() {
    if (!session || completing) return
    setCompleting(true)
    try {
      const transcript = messages.map(m =>
        `${m.role === 'assistant' ? 'Mickey' : 'User'}: ${m.content}`
      ).join('\n\n')

      const result = await api.post(`/kee/sessions/${session.id}/complete`, { transcript })

      const summary = [
        `Session complete!`,
        result.summary ? `\n**Summary:** ${result.summary}` : '',
        `\n**${result.cards?.length || 0}** knowledge cards extracted`,
        `**${result.painPoints?.length || 0}** pain points identified`,
        result.contradictions?.length ? `**${result.contradictions.length}** potential contradictions flagged` : '',
        result.nextSessionNotes ? `\n**Next time:** ${result.nextSessionNotes}` : '',
      ].filter(Boolean).join('\n')

      setMessages(prev => [...prev, { role: 'assistant', content: summary }])
      setSession(null)
      onComplete?.()
    } catch (err) {
      console.error('[KEE] Complete session error:', err)
    } finally {
      setCompleting(false)
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  // No active session — show start panel
  if (!session && messages.length === 0) {
    return (
      <div className="kee-start-panel">
        <div className="icon-ring">
          <Brain size={36} style={{ color: '#26A69A' }} />
        </div>
        <h2>
          {isMyself && mySession?.hasPendingSession
            ? `Welcome back, ${mySession.person.name}!`
            : 'Knowledge Extraction Session'}
        </h2>
        <p>
          {isMyself && mySession?.hasPendingSession
            ? `You have ${mySession.questions.length} questions ready. This should take about 3-5 minutes.`
            : 'Mickey will ask you about all aspects of the business — operations, strategy, culture, processes, and more. Your answers build the company\'s knowledge base.'}
        </p>

        {isMyself && mySession?.gamification && (
          <div className="kee-my-stats-mini">
            <div className="stat-item">
              <Flame size={14} style={{ color: '#FFA726' }} />
              <span>{mySession.gamification.current_streak} day streak</span>
            </div>
            <div className="stat-item">
              <BookOpen size={14} style={{ color: '#42A5F5' }} />
              <span>{mySession.gamification.total_knowledge_cards} cards</span>
            </div>
            <div className="stat-item">
              <Trophy size={14} style={{ color: getTitleInfo(mySession.gamification.current_title).color }} />
              <span>{getTitleInfo(mySession.gamification.current_title).label}</span>
            </div>
          </div>
        )}

        {!isMyself && (
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            {people.length > 0 && (
              <select
                value={selectedPerson}
                onChange={e => setSelectedPerson(e.target.value)}
                className="kee-select-inline"
              >
                {people.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            )}
          </div>
        )}

        <div className="kee-start-options">
          <button
            className="kee-start-btn primary"
            onClick={() => startSessionWithType('daily_pulse')}
            disabled={starting || !selectedPerson}
          >
            {starting ? <Loader2 size={16} className="kee-spin" /> : <Zap size={16} />}
            {isMyself && mySession?.hasPendingSession ? 'Start My Daily Session' : 'Daily Pulse (3 questions)'}
          </button>
          <button
            className="kee-start-btn secondary"
            onClick={() => startSessionWithType('deep_dive')}
            disabled={starting || !selectedPerson}
          >
            {starting ? <Loader2 size={16} className="kee-spin" /> : <Target size={16} />}
            Deep Dive (8 questions)
          </button>
        </div>
      </div>
    )
  }

  // Active session or completed
  return (
    <div className="kee-interview-panel">
      <div className="kee-interview-header">
        <div>
          <span style={{ fontWeight: 600, color: 'var(--color-text)' }}>
            {session ? `${session.session_type?.replace(/_/g, ' ')} Session` : 'Session Complete'}
          </span>
          {session && (
            <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginLeft: '0.75rem' }}>
              {session.questions_planned} questions planned
            </span>
          )}
        </div>
        {session && (
          <button
            className="kee-start-btn secondary"
            onClick={completeSession}
            disabled={completing || messages.length < 3}
            style={{ padding: '0.5rem 1rem', fontSize: '0.8125rem' }}
          >
            {completing ? <Loader2 size={14} /> : <Square size={14} />}
            End & Extract
          </button>
        )}
        {!session && (
          <button
            className="kee-start-btn primary"
            onClick={() => { setMessages([]); setSession(null) }}
            style={{ padding: '0.5rem 1rem', fontSize: '0.8125rem' }}
          >
            <Play size={14} /> New Session
          </button>
        )}
      </div>

      <div className="kee-messages">
        {messages.map((m, i) => (
          <div key={i} className={`kee-msg ${m.role}`}>
            {m.content}
          </div>
        ))}
        {sending && (
          <div className="kee-msg assistant" style={{ opacity: 0.6 }}>
            <Loader2 size={16} style={{ animation: 'kee-spin 1s linear infinite' }} />
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {session && (
        <>
        <div className="kee-input-row">
          <textarea
            ref={textareaRef}
            rows={2}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Answer the question..."
            disabled={sending}
          />
          <MicButton onTranscript={(text) => setInput(prev => (prev ? prev + ' ' : '') + text)} disabled={sending} />
          <button
            className="kee-send-btn"
            onClick={sendMessage}
            disabled={!input.trim() || sending}
          >
            <Send size={18} />
          </button>
        </div>
        <div style={{ display: 'flex', gap: '8px', marginTop: '8px', justifyContent: 'center' }}>
          <button
            onClick={() => { setInput("Not applicable to me. I do not have direct experience with this."); }}
            disabled={sending}
            style={{ padding: '4px 12px', fontSize: '12px', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: '6px', background: 'white', cursor: 'pointer' }}
          >
            Not applicable to me
          </button>
          <button
            onClick={() => { setInput("I would prefer not to answer this one. Next question please."); }}
            disabled={sending}
            style={{ padding: '4px 12px', fontSize: '12px', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: '6px', background: 'white', cursor: 'pointer' }}
          >
            Skip this question
          </button>
        </div>
        </>
      )}
    </div>
  )
}

// ────────────────────────────────────────────────
// KNOWLEDGE CARDS TAB
// ────────────────────────────────────────────────

function CardsTab() {
  const [cards, setCards] = useState([])
  const [loading, setLoading] = useState(true)
  const [category, setCategory] = useState('')
  const [contentType, setContentType] = useState('')

  useEffect(() => {
    setLoading(true)
    const params = new URLSearchParams()
    if (category) params.set('category', category)
    if (contentType) params.set('content_type', contentType)
    params.set('limit', '100')

    api.get(`/kee/cards?${params}`)
      .then(res => setCards(res.cards || []))
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [category, contentType])

  return (
    <>
      <div className="kee-filter-bar">
        <select value={category} onChange={e => setCategory(e.target.value)}>
          <option value="">All Categories</option>
          {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <select value={contentType} onChange={e => setContentType(e.target.value)}>
          <option value="">All Types</option>
          <option value="fact">Facts</option>
          <option value="procedure">Procedures</option>
          <option value="heuristic">Heuristics</option>
          <option value="preference">Preferences</option>
          <option value="story">Stories</option>
          <option value="opinion">Opinions</option>
        </select>
        <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginLeft: 'auto' }}>
          {cards.length} card{cards.length !== 1 ? 's' : ''}
        </span>
      </div>

      {loading ? (
        <div className="kee-loading"><Loader2 size={24} /></div>
      ) : cards.length === 0 ? (
        <div className="kee-empty">
          <BookOpen size={24} />
          <span>No knowledge cards yet. Complete an interview session to start extracting knowledge.</span>
        </div>
      ) : (
        <div className="kee-cards-grid">
          {cards.map(card => (
            <div className="kee-card" key={card.id}>
              <div className="kee-card-header">
                <span className="kee-card-title">{card.title}</span>
              </div>
              <div className="kee-card-body">{card.content}</div>
              <div className="kee-card-meta">
                <span className={`kee-badge type-${card.content_type}`}>
                  {card.content_type}
                </span>
                <span className={`kee-badge conf-${card.confidence}`}>
                  {card.confidence}
                </span>
                <span className="kee-badge">
                  {CATEGORY_LABELS[card.category] || card.category}
                </span>
                {card.source_person && (
                  <span className="kee-badge">{card.source_person.name}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  )
}

// ────────────────────────────────────────────────
// PAIN POINTS TAB
// ────────────────────────────────────────────────

function PainPointsTab() {
  const [painPoints, setPainPoints] = useState([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('')
  const [severityFilter, setSeverityFilter] = useState('')
  const [expanded, setExpanded] = useState(null)
  const [updating, setUpdating] = useState(null)

  useEffect(() => {
    setLoading(true)
    const params = new URLSearchParams()
    if (statusFilter) params.set('status', statusFilter)
    if (severityFilter) params.set('severity', severityFilter)

    api.get(`/kee/pain-points?${params}`)
      .then(res => setPainPoints(res.pain_points || []))
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [statusFilter, severityFilter])

  async function updatePainPoint(id, updates) {
    setUpdating(id)
    try {
      const res = await api.put(`/kee/pain-points/${id}`, updates)
      setPainPoints(prev => prev.map(pp => pp.id === id ? res.pain_point : pp))
    } catch (err) {
      console.error('[KEE] Update pain point error:', err)
    } finally {
      setUpdating(null)
    }
  }

  return (
    <>
      <div className="kee-filter-bar">
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="captured">Captured</option>
          <option value="validated">Validated</option>
          <option value="prioritized">Prioritized</option>
          <option value="in_progress">In Progress</option>
          <option value="resolved">Resolved</option>
        </select>
        <select value={severityFilter} onChange={e => setSeverityFilter(e.target.value)}>
          <option value="">All Severities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginLeft: 'auto' }}>
          {painPoints.length} pain point{painPoints.length !== 1 ? 's' : ''}
        </span>
      </div>

      {loading ? (
        <div className="kee-loading"><Loader2 size={24} /></div>
      ) : painPoints.length === 0 ? (
        <div className="kee-empty">
          <AlertTriangle size={24} />
          <span>No pain points captured yet. Pain points are automatically identified during interview sessions.</span>
        </div>
      ) : (
        <div className="kee-pain-points-list">
          {painPoints.map(pp => {
            const isExpanded = expanded === pp.id
            const sevColor = SEVERITY_COLORS[pp.severity] || SEVERITY_COLORS.medium
            const statColor = STATUS_COLORS[pp.status] || STATUS_COLORS.captured

            return (
              <div
                className={`kee-pain-point-card ${isExpanded ? 'expanded' : ''}`}
                key={pp.id}
                onClick={() => setExpanded(isExpanded ? null : pp.id)}
              >
                <div className="kee-pp-header">
                  <div className="kee-pp-title-row">
                    <AlertTriangle size={16} style={{ color: sevColor.color, flexShrink: 0 }} />
                    <span className="kee-pp-title">{pp.title}</span>
                  </div>
                  <div className="kee-pp-badges">
                    <span className="kee-badge" style={{ background: sevColor.bg, color: sevColor.color }}>
                      {pp.severity}
                    </span>
                    <span className="kee-badge" style={{ background: statColor.bg, color: statColor.color }}>
                      {pp.status?.replace(/_/g, ' ')}
                    </span>
                    <span className="kee-badge">{pp.category?.replace(/_/g, ' ')}</span>
                    {pp.source_person && (
                      <span className="kee-badge">{pp.source_person.name}</span>
                    )}
                    {pp.corroborated_count > 0 && (
                      <span className="kee-badge" style={{ background: '#AB47BC22', color: '#AB47BC' }}>
                        +{pp.corroborated_count} corroborated
                      </span>
                    )}
                  </div>
                </div>

                <div className="kee-pp-description">{pp.description}</div>

                {isExpanded && (
                  <div className="kee-pp-expanded" onClick={e => e.stopPropagation()}>
                    {pp.resolution && (
                      <div className="kee-pp-resolution">
                        <strong>Resolution:</strong> {pp.resolution}
                      </div>
                    )}
                    <div className="kee-pp-actions">
                      <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                        Update status:
                      </span>
                      {['captured', 'validated', 'prioritized', 'in_progress', 'resolved'].map(s => (
                        <button
                          key={s}
                          className={`kee-pp-action-btn ${pp.status === s ? 'active' : ''}`}
                          onClick={() => updatePainPoint(pp.id, { status: s })}
                          disabled={updating === pp.id || pp.status === s}
                        >
                          {s.replace(/_/g, ' ')}
                        </button>
                      ))}
                    </div>
                    <div className="kee-pp-actions">
                      <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                        Severity:
                      </span>
                      {['low', 'medium', 'high', 'critical'].map(s => (
                        <button
                          key={s}
                          className={`kee-pp-action-btn ${pp.severity === s ? 'active' : ''}`}
                          onClick={() => updatePainPoint(pp.id, { severity: s })}
                          disabled={updating === pp.id || pp.severity === s}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </>
  )
}

// ────────────────────────────────────────────────
// LEADERBOARD TAB
// ────────────────────────────────────────────────

function LeaderboardTab({ mySession }) {
  const [leaderboard, setLeaderboard] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api.get('/kee/leaderboard')
      .then(res => setLeaderboard(res.leaderboard || []))
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [])

  if (loading) {
    return <div className="kee-loading"><Loader2 size={24} /></div>
  }

  const myPersonId = mySession?.person?.id

  return (
    <>
      {/* Title progression visual */}
      <div className="kee-title-progression">
        <div className="kee-section-title" style={{ marginBottom: '0.75rem' }}>Title Progression</div>
        <div className="kee-title-track">
          {TITLE_PROGRESSION.map((t, i) => {
            const isActive = mySession?.gamification?.current_title === t.key
            const isPast = TITLE_PROGRESSION.findIndex(x => x.key === mySession?.gamification?.current_title) >= i
            return (
              <div key={t.key} className={`kee-title-step ${isActive ? 'active' : ''} ${isPast ? 'past' : ''}`}>
                <div
                  className="kee-title-dot"
                  style={{
                    background: isPast ? t.color : 'var(--color-bg)',
                    borderColor: t.color,
                  }}
                >
                  {isActive && <Star size={12} style={{ color: 'white' }} />}
                </div>
                <span className="kee-title-label" style={{ color: isPast ? t.color : 'var(--color-text-muted)' }}>
                  {t.label}
                </span>
                <span className="kee-title-req">{t.minSessions}+ sessions</span>
                {i < TITLE_PROGRESSION.length - 1 && (
                  <div className="kee-title-connector" style={{ background: isPast ? t.color : 'var(--color-border)' }} />
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* My stats card */}
      {mySession?.gamification && (
        <div className="kee-my-stats-card">
          <div className="kee-my-stats-header">
            <div className="kee-person-avatar" style={{ width: 48, height: 48, fontSize: '1rem' }}>
              {getInitials(mySession.person.name)}
            </div>
            <div>
              <div style={{ fontWeight: 600, color: 'var(--color-text)' }}>{mySession.person.name}</div>
              <div style={{ fontSize: '0.75rem', color: getTitleInfo(mySession.gamification.current_title).color, fontWeight: 500 }}>
                {getTitleInfo(mySession.gamification.current_title).label}
              </div>
            </div>
          </div>
          <div className="kee-my-stats-grid">
            <div className="kee-my-stat">
              <Flame size={18} style={{ color: '#FFA726' }} />
              <div className="num">{mySession.gamification.current_streak}</div>
              <div className="lbl">Current Streak</div>
            </div>
            <div className="kee-my-stat">
              <Trophy size={18} style={{ color: '#66BB6A' }} />
              <div className="num">{mySession.gamification.longest_streak_alltime}</div>
              <div className="lbl">Best Streak</div>
            </div>
            <div className="kee-my-stat">
              <MessageCircle size={18} style={{ color: '#42A5F5' }} />
              <div className="num">{mySession.gamification.total_sessions_alltime}</div>
              <div className="lbl">Sessions</div>
            </div>
            <div className="kee-my-stat">
              <BookOpen size={18} style={{ color: '#AB47BC' }} />
              <div className="num">{mySession.gamification.total_knowledge_cards}</div>
              <div className="lbl">Cards</div>
            </div>
          </div>
        </div>
      )}

      {/* Leaderboard list */}
      <div className="kee-section-header" style={{ marginTop: '1.5rem' }}>
        <div className="kee-section-title">Rankings</div>
      </div>

      {leaderboard.length === 0 ? (
        <div className="kee-empty">
          <Award size={24} />
          <span>No leaderboard data yet. Complete sessions to appear on the leaderboard.</span>
        </div>
      ) : (
        <div className="kee-leaderboard-list">
          {leaderboard.map((entry, i) => {
            const titleInfo = getTitleInfo(entry.current_title)
            const isMe = entry.person_id === myPersonId

            return (
              <div className={`kee-leaderboard-row ${isMe ? 'is-me' : ''}`} key={entry.id}>
                <div className="kee-lb-rank">
                  {i === 0 ? <Trophy size={18} style={{ color: '#FFD700' }} /> :
                   i === 1 ? <Trophy size={18} style={{ color: '#C0C0C0' }} /> :
                   i === 2 ? <Trophy size={18} style={{ color: '#CD7F32' }} /> :
                   <span>{i + 1}</span>}
                </div>
                <div className="kee-person-avatar" style={{ width: 36, height: 36, fontSize: '0.8125rem' }}>
                  {getInitials(entry.person?.name)}
                </div>
                <div className="kee-lb-info">
                  <div className="kee-lb-name">
                    {entry.person?.name || 'Unknown'}
                    {isMe && <span className="kee-you-badge">YOU</span>}
                  </div>
                  <div className="kee-lb-title" style={{ color: titleInfo.color }}>
                    {titleInfo.label}
                  </div>
                </div>
                <div className="kee-lb-stats">
                  <div className="kee-lb-stat">
                    <Flame size={14} style={{ color: '#FFA726' }} />
                    <span>{entry.current_streak}</span>
                  </div>
                  <div className="kee-lb-stat">
                    <MessageCircle size={14} style={{ color: '#42A5F5' }} />
                    <span>{entry.total_sessions_alltime}</span>
                  </div>
                  <div className="kee-lb-stat">
                    <BookOpen size={14} style={{ color: '#66BB6A' }} />
                    <span>{entry.total_knowledge_cards}</span>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </>
  )
}

// ────────────────────────────────────────────────
// SOPs TAB
// ────────────────────────────────────────────────

function SOPsTab() {
  const [sops, setSOPs] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedSOP, setSelectedSOP] = useState(null)
  const [generating, setGenerating] = useState(false)
  const [genCategory, setGenCategory] = useState('')
  const [showGenerate, setShowGenerate] = useState(false)
  const [updating, setUpdating] = useState(null)

  useEffect(() => {
    api.get('/kee/sops')
      .then(res => setSOPs(res.sops || []))
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [])

  async function generateSOP() {
    if (!genCategory || generating) return
    setGenerating(true)
    try {
      const res = await api.post('/kee/sops/generate', { category: genCategory })
      setSOPs(prev => [res.sop, ...prev])
      setSelectedSOP(res.sop)
      setShowGenerate(false)
      setGenCategory('')
    } catch (err) {
      console.error('[KEE] Generate SOP error:', err)
      alert(err.message || 'Failed to generate SOP')
    } finally {
      setGenerating(false)
    }
  }

  async function updateSOP(id, updates) {
    setUpdating(id)
    try {
      const res = await api.put(`/kee/sops/${id}`, updates)
      setSOPs(prev => prev.map(s => s.id === id ? res.sop : s))
      if (selectedSOP?.id === id) setSelectedSOP(res.sop)
    } catch (err) {
      console.error('[KEE] Update SOP error:', err)
    } finally {
      setUpdating(null)
    }
  }

  const SOP_STATUS_COLORS = {
    draft: { bg: '#78909C22', color: '#78909C' },
    pending_review: { bg: '#FFA72622', color: '#FFA726' },
    approved: { bg: '#66BB6A22', color: '#66BB6A' },
  }

  if (selectedSOP) {
    const stColor = SOP_STATUS_COLORS[selectedSOP.status] || SOP_STATUS_COLORS.draft
    return (
      <div className="kee-sop-detail">
        <button
          className="kee-start-btn secondary"
          onClick={() => setSelectedSOP(null)}
          style={{ padding: '0.5rem 1rem', fontSize: '0.8125rem', marginBottom: '1rem' }}
        >
          <ChevronRight size={14} style={{ transform: 'rotate(180deg)' }} /> Back to SOPs
        </button>

        <div className="kee-sop-detail-header">
          <h2 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--color-text)', margin: 0 }}>
            {selectedSOP.title}
          </h2>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <span className="kee-badge" style={{ background: stColor.bg, color: stColor.color }}>
              {selectedSOP.status?.replace(/_/g, ' ')}
            </span>
            <span className="kee-badge">{CATEGORY_LABELS[selectedSOP.category] || selectedSOP.category}</span>
          </div>
        </div>

        <div className="kee-sop-content">
          <div
            className="kee-sop-markdown"
            dangerouslySetInnerHTML={{ __html: renderBasicMarkdown(selectedSOP.content) }}
          />
        </div>

        <div className="kee-sop-actions">
          {selectedSOP.status === 'draft' && (
            <button
              className="kee-start-btn primary"
              onClick={() => updateSOP(selectedSOP.id, { status: 'pending_review' })}
              disabled={updating === selectedSOP.id}
              style={{ padding: '0.5rem 1rem', fontSize: '0.8125rem' }}
            >
              <Eye size={14} /> Submit for Review
            </button>
          )}
          {selectedSOP.status === 'pending_review' && (
            <>
              <button
                className="kee-start-btn primary"
                onClick={() => updateSOP(selectedSOP.id, { status: 'approved', approved_at: new Date().toISOString() })}
                disabled={updating === selectedSOP.id}
                style={{ padding: '0.5rem 1rem', fontSize: '0.8125rem' }}
              >
                <ThumbsUp size={14} /> Approve
              </button>
              <button
                className="kee-start-btn secondary"
                onClick={() => updateSOP(selectedSOP.id, { status: 'draft' })}
                disabled={updating === selectedSOP.id}
                style={{ padding: '0.5rem 1rem', fontSize: '0.8125rem' }}
              >
                <Edit3 size={14} /> Request Revision
              </button>
            </>
          )}
          {selectedSOP.status === 'approved' && (
            <span style={{ fontSize: '0.8125rem', color: '#66BB6A', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <CheckCircle size={14} /> Approved
            </span>
          )}
        </div>

        {selectedSOP.source_cards?.length > 0 && (
          <div style={{ marginTop: '1rem', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
            Generated from {selectedSOP.source_cards.length} knowledge card{selectedSOP.source_cards.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      <div className="kee-section-header">
        <div className="kee-section-title">Standard Operating Procedures</div>
        <button
          className="kee-start-btn secondary"
          onClick={() => setShowGenerate(!showGenerate)}
          style={{ padding: '0.5rem 1rem', fontSize: '0.8125rem' }}
        >
          {showGenerate ? <X size={14} /> : <Plus size={14} />}
          {showGenerate ? 'Cancel' : 'Generate SOP'}
        </button>
      </div>

      {showGenerate && (
        <div className="kee-surface-card" style={{ marginBottom: '1rem' }}>
          <div style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--color-text)', marginBottom: '0.75rem' }}>
            Generate SOP from Knowledge Cards
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <select
              value={genCategory}
              onChange={e => setGenCategory(e.target.value)}
              className="kee-select-inline"
              style={{ flex: 1 }}
            >
              <option value="">Select a category...</option>
              {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <button
              className="kee-start-btn primary"
              onClick={generateSOP}
              disabled={!genCategory || generating}
              style={{ padding: '0.5rem 1rem', fontSize: '0.8125rem', whiteSpace: 'nowrap' }}
            >
              {generating ? <Loader2 size={14} className="kee-spin" /> : <Zap size={14} />}
              {generating ? 'Generating...' : 'Generate'}
            </button>
          </div>
          <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: '0.5rem' }}>
            AI will synthesize all knowledge cards in the selected category into a comprehensive SOP.
          </p>
        </div>
      )}

      {loading ? (
        <div className="kee-loading"><Loader2 size={24} /></div>
      ) : sops.length === 0 ? (
        <div className="kee-empty">
          <FileText size={24} />
          <span>No SOPs generated yet. Generate an SOP from accumulated knowledge cards.</span>
        </div>
      ) : (
        <div className="kee-sops-list">
          {sops.map(sop => {
            const stColor = SOP_STATUS_COLORS[sop.status] || SOP_STATUS_COLORS.draft
            return (
              <div
                className="kee-sop-row"
                key={sop.id}
                onClick={() => setSelectedSOP(sop)}
              >
                <FileText size={18} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500, color: 'var(--color-text)', fontSize: '0.875rem' }}>
                    {sop.title}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: '0.125rem' }}>
                    {CATEGORY_LABELS[sop.category] || sop.category} — {formatRelativeTime(sop.created_at)}
                  </div>
                </div>
                <span className="kee-badge" style={{ background: stColor.bg, color: stColor.color }}>
                  {sop.status?.replace(/_/g, ' ')}
                </span>
                <ChevronRight size={16} style={{ color: 'var(--color-text-muted)' }} />
              </div>
            )
          })}
        </div>
      )}
    </>
  )
}

// Basic markdown renderer for SOP content — all content is HTML-escaped first
function renderBasicMarkdown(text) {
  if (!text) return ''
  // Escape ALL HTML entities first to prevent XSS
  let safe = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

  // Now apply markdown formatting on the escaped text (safe because content is escaped)
  safe = safe
    .replace(/^## (.+)$/gm, '<h3>$1</h3>')
    .replace(/^### (.+)$/gm, '<h4>$1</h4>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/\[GAP: (.+?)\]/g, '<span class="kee-gap-marker">[GAP: $1]</span>')
    .replace(/\n{2,}/g, '<br/><br/>')
    .replace(/\n/g, '<br/>')
  return safe
}

// ────────────────────────────────────────────────
// PEOPLE TAB (with Position Descriptions)
// ────────────────────────────────────────────────

function PeopleTab({ people, onRefresh }) {
  const [showAdd, setShowAdd] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    name: '', title: '', department: '', role_type: 'individual_contributor',
    role_category: 'operations', contact_email: '', is_kee_participant: true,
  })
  const [personStats, setPersonStats] = useState({})
  const [positions, setPositions] = useState({})
  const [expandedPerson, setExpandedPerson] = useState(null)
  const [generatingPD, setGeneratingPD] = useState(null)
  const [viewingPD, setViewingPD] = useState(null)

  useEffect(() => {
    people.forEach(p => {
      if (!personStats[p.id]) {
        api.get(`/kee/people/${p.id}`)
          .then(res => setPersonStats(prev => ({ ...prev, [p.id]: res.stats })))
          .catch(() => {})
      }
    })
    // Load all position descriptions
    api.get('/kee/positions')
      .then(res => {
        const posMap = {}
        for (const pos of res.positions || []) {
          posMap[pos.person_id] = pos
        }
        setPositions(posMap)
      })
      .catch(() => {})
  }, [people])

  async function addPerson(e) {
    e.preventDefault()
    if (!form.name.trim()) return
    setSaving(true)
    try {
      await api.post('/kee/people', form)
      setForm({ name: '', title: '', department: '', role_type: 'individual_contributor', role_category: 'operations', contact_email: '', is_kee_participant: true })
      setShowAdd(false)
      onRefresh()
    } catch (err) {
      console.error('[KEE] Add person error:', err)
    } finally {
      setSaving(false)
    }
  }

  async function generatePositionDesc(personId) {
    setGeneratingPD(personId)
    try {
      const res = await api.post('/kee/positions/generate', { person_id: personId })
      setPositions(prev => ({ ...prev, [personId]: res.position }))
      setViewingPD(personId)
    } catch (err) {
      console.error('[KEE] Generate position desc error:', err)
      alert(err.message || 'Failed to generate position description')
    } finally {
      setGeneratingPD(null)
    }
  }

  async function updatePositionStatus(posId, status) {
    try {
      const updates = { status }
      if (status === 'approved') updates.approved_at = new Date().toISOString()
      const res = await api.put(`/kee/positions/${posId}`, updates)
      setPositions(prev => {
        const updated = { ...prev }
        for (const key of Object.keys(updated)) {
          if (updated[key]?.id === posId) updated[key] = res.position
        }
        return updated
      })
    } catch (err) {
      console.error('[KEE] Update position error:', err)
    }
  }

  // Viewing a position description
  if (viewingPD && positions[viewingPD]) {
    const pos = positions[viewingPD]
    const person = people.find(p => p.id === viewingPD)
    const PD_STATUS = {
      draft: { bg: '#78909C22', color: '#78909C' },
      approved: { bg: '#66BB6A22', color: '#66BB6A' },
    }
    const stColor = PD_STATUS[pos.status] || PD_STATUS.draft

    return (
      <>
        <button
          className="kee-start-btn secondary"
          onClick={() => setViewingPD(null)}
          style={{ padding: '0.5rem 1rem', fontSize: '0.8125rem', marginBottom: '1rem' }}
        >
          <ChevronRight size={14} style={{ transform: 'rotate(180deg)' }} /> Back to People
        </button>

        <div className="kee-sop-detail-header">
          <h2 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--color-text)', margin: 0 }}>
            Position Description — {person?.name || 'Unknown'}
          </h2>
          <span className="kee-badge" style={{ background: stColor.bg, color: stColor.color }}>
            {pos.status}
          </span>
        </div>

        <div className="kee-sop-content">
          <div
            className="kee-sop-markdown"
            dangerouslySetInnerHTML={{ __html: renderBasicMarkdown(pos.content) }}
          />
        </div>

        <div className="kee-sop-actions">
          {pos.status === 'draft' && (
            <>
              <button
                className="kee-start-btn primary"
                onClick={() => updatePositionStatus(pos.id, 'approved')}
                style={{ padding: '0.5rem 1rem', fontSize: '0.8125rem' }}
              >
                <ThumbsUp size={14} /> Approve
              </button>
              <button
                className="kee-start-btn secondary"
                onClick={() => generatePositionDesc(viewingPD)}
                disabled={generatingPD === viewingPD}
                style={{ padding: '0.5rem 1rem', fontSize: '0.8125rem' }}
              >
                {generatingPD === viewingPD ? <Loader2 size={14} className="kee-spin" /> : <RefreshCw size={14} />}
                Regenerate
              </button>
            </>
          )}
          {pos.status === 'approved' && (
            <span style={{ fontSize: '0.8125rem', color: '#66BB6A', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <CheckCircle size={14} /> Approved
            </span>
          )}
        </div>
      </>
    )
  }

  return (
    <>
      <div className="kee-section-header">
        <div className="kee-section-title">KEE Participants</div>
        <button
          className="kee-start-btn secondary"
          onClick={() => setShowAdd(!showAdd)}
          style={{ padding: '0.5rem 1rem', fontSize: '0.8125rem' }}
        >
          {showAdd ? <X size={14} /> : <Plus size={14} />}
          {showAdd ? 'Cancel' : 'Add Person'}
        </button>
      </div>

      {showAdd && (
        <form className="kee-add-form" onSubmit={addPerson}>
          <div className="form-grid">
            <label>
              Name *
              <input
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Full name"
                required
              />
            </label>
            <label>
              Title
              <input
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Job title"
              />
            </label>
            <label>
              Department
              <input
                value={form.department}
                onChange={e => setForm(f => ({ ...f, department: e.target.value }))}
                placeholder="e.g. Operations"
              />
            </label>
            <label>
              Role Type
              <select
                value={form.role_type}
                onChange={e => setForm(f => ({ ...f, role_type: e.target.value }))}
              >
                {ROLE_TYPES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </label>
            <label>
              Role Category
              <select
                value={form.role_category}
                onChange={e => setForm(f => ({ ...f, role_category: e.target.value }))}
              >
                {ROLE_CATEGORIES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </label>
            <label>
              Email
              <input
                type="email"
                value={form.contact_email}
                onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))}
                placeholder="email@company.com"
              />
            </label>
          </div>
          <div className="form-actions">
            <button
              type="submit"
              className="kee-start-btn primary"
              disabled={saving || !form.name.trim()}
              style={{ padding: '0.5rem 1rem', fontSize: '0.8125rem' }}
            >
              {saving ? <Loader2 size={14} /> : <Plus size={14} />}
              Add Participant
            </button>
          </div>
        </form>
      )}

      {people.length === 0 ? (
        <div className="kee-empty">
          <Users size={24} />
          <span>No people added yet. Add team members to start knowledge extraction.</span>
        </div>
      ) : (
        <div className="kee-people-list">
          {people.map(p => {
            const st = personStats[p.id]
            const pos = positions[p.id]
            const posStatus = pos?.status || 'none'

            return (
              <div className="kee-person-row-wrapper" key={p.id}>
                <div className="kee-person-row" onClick={() => setExpandedPerson(expandedPerson === p.id ? null : p.id)}>
                  <div className="kee-person-avatar">{getInitials(p.name)}</div>
                  <div className="kee-person-info">
                    <div className="kee-person-name">{p.name}</div>
                    <div className="kee-person-role">
                      {p.title || p.role_type} — {p.department || 'No department'}
                    </div>
                  </div>
                  <div className="kee-person-stats">
                    <div className="kee-person-stat">
                      <div className="num">{st?.totalSessions ?? '—'}</div>
                      <div className="lbl">Sessions</div>
                    </div>
                    <div className="kee-person-stat">
                      <div className="num">{st?.totalCards ?? '—'}</div>
                      <div className="lbl">Cards</div>
                    </div>
                    <div className="kee-person-stat">
                      <div className="num">{st?.gamification?.current_streak ?? 0}</div>
                      <div className="lbl">Streak</div>
                    </div>
                  </div>
                  <div className="kee-pd-status-badge" data-status={posStatus}>
                    {posStatus === 'none' ? 'No PD' : posStatus === 'draft' ? 'Draft PD' : 'Approved PD'}
                  </div>
                  <ChevronRight
                    size={16}
                    style={{
                      color: 'var(--color-text-muted)',
                      flexShrink: 0,
                      transform: expandedPerson === p.id ? 'rotate(90deg)' : 'none',
                      transition: 'transform 0.2s',
                    }}
                  />
                </div>

                {expandedPerson === p.id && (
                  <div className="kee-person-expanded">
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--color-text)' }}>
                        Position Description
                      </span>
                      {pos ? (
                        <>
                          <button
                            className="kee-start-btn secondary"
                            onClick={() => setViewingPD(p.id)}
                            style={{ padding: '0.375rem 0.75rem', fontSize: '0.75rem' }}
                          >
                            <Eye size={12} /> View
                          </button>
                          <button
                            className="kee-start-btn secondary"
                            onClick={() => generatePositionDesc(p.id)}
                            disabled={generatingPD === p.id}
                            style={{ padding: '0.375rem 0.75rem', fontSize: '0.75rem' }}
                          >
                            {generatingPD === p.id ? <Loader2 size={12} className="kee-spin" /> : <RefreshCw size={12} />}
                            Regenerate
                          </button>
                        </>
                      ) : (
                        <button
                          className="kee-start-btn primary"
                          onClick={() => generatePositionDesc(p.id)}
                          disabled={generatingPD === p.id}
                          style={{ padding: '0.375rem 0.75rem', fontSize: '0.75rem' }}
                        >
                          {generatingPD === p.id ? <Loader2 size={12} className="kee-spin" /> : <Zap size={12} />}
                          Generate
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </>
  )
}
